import { useContext, useState } from "react";

import { API } from "constants/RestApi";
import WorkOrderContext from "context/WorkOrderContext";
import { useBaseAction } from "hooks/core";

export const DESCRIPTION_UPDATED_MESSAGE = "Description has been updated";

/**
 * Owns the "edit work order description" use case: modal visibility, the value
 * being edited, the update request and the work order refresh that follows it.
 */
const useEditWorkOrderDescription = () => {
  const { request } = useBaseAction();
  const { workOrder, getWorkOrder } = useContext(WorkOrderContext);
  const [descriptionToEdit, setDescriptionToEdit] = useState<string | null>(null);

  const closeModal = () => setDescriptionToEdit(null);

  // Snapshot the value on open so a background work order refresh cannot
  // overwrite the draft the user is typing.
  const openModal = () => setDescriptionToEdit(workOrder?.description ?? "");

  const saveDescription = async (description: string) => {
    if (!workOrder) return;

    await request(
      {
        method: "PUT",
        url: API.WORK_ORDERS,
        body: {
          id: workOrder.id,
          rowVersion: workOrder.rowVersion,
          description: description.trim(),
        },
        successMessage: DESCRIPTION_UPDATED_MESSAGE,
        useLoader: true,
      },
      async () => {
        closeModal();
        await getWorkOrder(false);
      },
    );
  };

  return {
    descriptionToEdit,
    isModalOpen: descriptionToEdit !== null,
    openModal,
    closeModal,
    saveDescription,
  };
};

export default useEditWorkOrderDescription;
